'use client';

import type { FC } from 'react';
import type { IconName } from '@/components/entities/Sprite/Icon';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { notFound } from 'next/navigation';
import { useState } from 'react';
import EmptyGamesState from '@/components/entities/games/EmptyGamesState';
import GameCard from '@/components/entities/games/GameCard';
import GameSkeleton from '@/components/entities/games/GameCard/components/GameSkeleton';
import { GamesSearchBar } from '@/components/entities/games/GamesSearchBar';
import { Icon } from '@/components/entities/Sprite';
import { useCampaignCategory } from '@/components/features/campaigns';
import Pagination from '@/components/shared/Pagination';
import { SkeletonParent } from '@/components/ui/Skeleton/skeleton';
import { CATEGORY_ICONS } from '@/consts/CategoryIcons';
import { SlotsFilledAliases } from '@/consts/seo/SlotsAliases';
import { useIsMobile } from '@/hooks/useIsMobile';
import { AppRoutes } from '@/i18n/constants';
import { isStaging } from '@/libs/getEnvironment';
import { queryCategoryById } from '@/queries/categories/categories.queries';
import { gameQueryOptions } from '@/queries/games/games.queries';
import { PUBLIC_QUERY_STALE_TIME } from '@/queries/queryOptions.helpers';
import { cn } from '@/utils/cn';
import { withSearchQuery } from '@/utils/paginationHref.utils';
import { capitalizeWords } from '@/utils/texts.utils';

interface CategoryViewProps {
  categoryId: string;
  isAllGamesCategory?: boolean;
  page: number;
}

const CategoryView: FC<CategoryViewProps> = ({ categoryId, isAllGamesCategory, page }) => {
  const t = useTranslations('lobby.filters');
  const [searchQuery, setSearchQuery] = useState('');
  const isStagingEnv = isStaging();
  const isMobile = useIsMobile();
  const visibleGamesCount = isMobile ? 18 : 32;

  const { data: category, isFetching: isCategoryFetching } = useSuspenseQuery(queryCategoryById(categoryId));
  const campaignCategory = useCampaignCategory();
  // The Campaign Category has no fixed id to map, so its icon comes from the Skin, as in the sidebar.
  const categoryIcon = campaignCategory?.category.id === categoryId
    ? campaignCategory.icon
    : CATEGORY_ICONS[categoryId] as IconName;

  if (!category) {
    notFound();
  }

  const { data: gamesData, isFetching: isGamesFetching } = useQuery(
    gameQueryOptions.listByCategory({
      page,
      categoryId,
      searchValue: searchQuery,
      options: { staleTime: PUBLIC_QUERY_STALE_TIME },
    }),
  );

  const isGamesEmpty = gamesData?.items && gamesData.items.length === 0;
  const showSkeletons = isGamesFetching;
  const displayedGames = gamesData?.items.slice(0, visibleGamesCount);
  const hasPagination = !isGamesEmpty && !!gamesData?.totalPages && gamesData.totalPages > 1;
  const gridClassName = cn(
    'grid mt-3 lg:mt-3 grid-cols-3 gap-x-3.5 gap-y-2.25 md:grid-cols-4 md:gap-5 lg:grid-cols-[repeat(auto-fill,7.5rem)] xxl:grid-cols-[repeat(8,minmax(0,7.5rem))] lg:justify-between lg:gap-x-6 lg:gap-y-4 w-full',
    !hasPagination && 'lg:-mb-0.75',
  );

  return (
    <>
      <div className="flex items-center justify-between gap-4 relative min-h-11">
        {!isCategoryFetching
          ? (
              <h1 className="text-white text-[1.125rem] leading-5.5 font-medium flex items-center gap-1.5">
                <Icon
                  name={categoryIcon}
                  className="size-6"
                />
                {capitalizeWords(category?.name ?? '')}
              </h1>
            )
          : (
              <SkeletonParent className="h-7 w-75" />
            )}

        <GamesSearchBar
          placeholder={t('search-placeholder')}
          resetPageOnSearch={false}
          onSearchChange={setSearchQuery}
        />
      </div>

      {showSkeletons
        ? (
            <div className={gridClassName}>
              {Array.from({ length: visibleGamesCount }).map((_, idx) => (
                <GameSkeleton key={idx} />
              ))}
            </div>
          )
        : (
            <>
              {isGamesEmpty && gamesData?.items && (
                <EmptyGamesState isSearch={!!searchQuery} />
              )}

              {!isGamesEmpty && (
                <div className={gridClassName}>
                  {displayedGames?.map((game) => {
                    const isFilledAlias = SlotsFilledAliases.includes(game.alias);
                    const shouldUseSlotsSlug = Boolean(isAllGamesCategory) && (isStagingEnv || isFilledAlias);

                    return (
                      <GameCard
                        key={game.id}
                        id={game.id}
                        name={game.name}
                        cover={game.cover}
                        likeButtonPosition="compactLg"
                        likeButtonIconSize="compactLg"
                        path={shouldUseSlotsSlug ? AppRoutes.slotsSlug(game.alias) : AppRoutes.gamesId(game.id)}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}

      {hasPagination && (
        <Pagination
          variant="link"
          pageNumber={page}
          totalItems={gamesData?.totalItems ?? 0}
          totalPages={gamesData?.totalPages ?? 0}
          className="mt-5 md:ml-0 lg:mb-px"
          getHrefAction={nextPage => withSearchQuery(AppRoutes.categoryIdPaged(categoryId, nextPage), searchQuery, 'search')}
        />
      )}
    </>
  );
};

export default CategoryView;
